import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ADD_FAVORITE_PROFESSIONAL,
  BLOCK_RECENT_PROFESSIONAL,
  FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL,
  FETCH_RECENT_PROFESSIONALS,
  fetchRecentProfessionals,
} from '../../../../actions';
import { createSuccessSelector } from '../../../../api/selectors';
import CustomSpinner from '../../../../commonComponents/CustomSpinner';
import useLoading from '../../../../hooks/useLoading';
import { Colors } from '../../../../themes/colors';
import Tooltip from '../../../Tooltip';
import { ProfessionalCard } from '../ProfessionalCard';
import { EmptyRecentProfessionals } from './EmptyRecentProfessionals';
import { ProfessionalCardActions } from './ProfessionalCardActions';
import { ProfessionalCardDetails } from './ProfessionalCardDetails';

const actions = [
  FETCH_RECENT_PROFESSIONALS,
  ADD_FAVORITE_PROFESSIONAL,
  BLOCK_RECENT_PROFESSIONAL,
  FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL,
];

const successAddToFavoritesSelector = createSuccessSelector([ADD_FAVORITE_PROFESSIONAL]);
const successBlockProfessionalSelector = createSuccessSelector([BLOCK_RECENT_PROFESSIONAL]);

export const RecentProfessionalsList = () => {
  const dispatch = useDispatch();
  const { isLoading } = useLoading(actions);
  const recentProfessionals = useSelector((state) => state.user.recentProfessionals);
  const user = useSelector((state) => state.user.selectedChildOffice ?? state.user.user);
  const isRecentProfessionalsTabNew = localStorage.getItem(
    `isRecentProfessionalsTabNew-${user.id}`,
  );
  const isAddedToFavoritesSuccess = useSelector((state) => successAddToFavoritesSelector(state));
  const isBlockedProfessionalSuccess = useSelector((state) =>
    successBlockProfessionalSelector(state),
  );

  useEffect(() => {
    dispatch(fetchRecentProfessionals());
  }, [dispatch, isAddedToFavoritesSuccess, isBlockedProfessionalSuccess]);

  useEffect(() => {
    if (!isRecentProfessionalsTabNew) {
      localStorage.setItem(`isRecentProfessionalsTabNew-${user.id}`, false);
    }
  }, [isRecentProfessionalsTabNew, user]);

  if (isLoading) {
    return <CustomSpinner isLoading size={24} />;
  }

  if (!recentProfessionals?.length) {
    return <EmptyRecentProfessionals />;
  }

  return (
    <div
      style={{
        width: '100%',
      }}
    >
      <div
        style={{
          position: 'relative',
          fontFamily: 'Nunito',
          fontSize: '16px',
          fontStyle: 'italic',
          color: Colors.neutral_500,
          textAlign: 'center',
        }}
      >
        This section of the Professionals Hub shows the most recent professionals to have worked at
        your
        <br />
        office. From this page, you can block professionals, add them to your favorites, or directly
        invite
        <br />
        professionals to your upcoming shifts!
        <span
          style={{
            marginLeft: '5px',
            position: 'absolute',
            fontStyle: 'normal',
            fontWeight: 700,
          }}
        >
          <Tooltip content="The recent professionals list displays the last 4 professionals to have completed shifts at your office. This list excludes favorites and blocked professionals." />
        </span>
      </div>

      <div
        style={{
          maxWidth: '827px',
          marginTop: 32,
          display: 'flex',
          justifyContent: 'center',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <motion.div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
          layout
        >
          <AnimatePresence>
            {recentProfessionals?.map((job) => (
              <ProfessionalCard
                key={job.id}
                user={{
                  id: job?.professional?.id,
                  fullname: job?.professional?.full_name,
                  profession: job.profession,
                  profileImage: job.professional?.image,
                  datetime: job?.job_datetime,
                  isAvailable: true,
                }}
                Details={ProfessionalCardDetails}
                Actions={ProfessionalCardActions}
                showRemoveIcon={false}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};
